import { type ComponentPropsWithRef, memo } from 'react';

import { CheckIcon, XIcon } from '@air/design-icons';
import { IconButton } from '@air/primitive-icon-button';
import { VisuallyHidden } from '@air/primitive-visually-hidden';
import { cn } from '@air/utils-tailwind';

import { getContrastColor } from '~/components/v2/utils/filters/getContrastColor';

export type CustomColorValueProps = ComponentPropsWithRef<'div'> & {
  hex: string;
  isSelected?: boolean;
  onSelect: () => void;
  onRemoveHex: () => void;
};

export const CustomColorValue = memo(
  ({ className, hex, isSelected, onSelect, onRemoveHex, ...restOfProps }: CustomColorValueProps) => {
    return (
      <div
        className={cn(
          'relative flex h-6 items-center gap-1 rounded-24 border ads:border-ads-gray-5 border-gray-5 px-1 uppercase ring-0 transition-colors ads:hover:bg-hover-subtle hover:bg-gray-4 ads:data-[selected=true]:border-ads-gray-10 data-[selected=true]:border-gray-10 ads:data-[selected=true]:ring-ads-gray-10 data-[selected=true]:ring-1 data-[selected=true]:ring-gray-10 ads:data-[selected=true]:hover:bg-hover-subtle data-[selected=true]:hover:bg-gray-4',
          className,
        )}
        data-selected={isSelected}
        data-hex={hex}
        data-testid="CUSTOM_COLOR_CHIP"
        {...restOfProps}
      >
        <button className="absolute inset-0 size-full" onClick={onSelect} type="button">
          <VisuallyHidden>Select {hex}</VisuallyHidden>
        </button>
        <div className="flex size-4 items-center justify-center rounded-full" style={{ backgroundColor: hex }}>
          {isSelected && <CheckIcon size="xs" style={{ color: getContrastColor(hex) }} />}
        </div>
        <span className="font-medium ads:text-primary text-12 text-gray-12">{hex}</span>
        <IconButton
          appearance="ghost"
          color="gray"
          data-hex={hex}
          data-testid="CUSTOM_COLOR_REMOVE_BUTTON"
          adsIcon
          icon={XIcon}
          label={`Remove ${hex}`}
          onClick={onRemoveHex}
          size="extra-small"
        />
      </div>
    );
  },
);

CustomColorValue.displayName = 'CustomColorValue';
